import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

export type Sport = 'nfl' | 'mlb' | 'nba' | 'nhl' | 'golf';

export interface SportConfig {
  id: Sport;
  label: string;
  emoji: string;
  enabled: boolean;
  seasonLabel: string;
}

// SAB-824: seasonLabel is display copy, not a query filter, and is deliberately a
// literal -- there is no per-sport season source of truth to read it from. It therefore
// has to be updated by hand each rollover. NFL sat on '2025-26' until 2026-08-28 while
// MLB beside it already read '2026'; a mismatched pair on the sport switcher is the
// cheapest possible tell that this needs a look, so keep them in step.
export const SPORTS: SportConfig[] = [
  { id: 'nfl', label: 'NFL', emoji: '🏈', enabled: true, seasonLabel: '2026-27 Season' },
  { id: 'mlb', label: 'MLB', emoji: '⚾', enabled: true, seasonLabel: '2026 Season' },
  { id: 'nba', label: 'NBA', emoji: '🏀', enabled: false, seasonLabel: 'Coming Soon' },
  { id: 'nhl', label: 'NHL', emoji: '🏒', enabled: false, seasonLabel: 'Coming Soon' },
  { id: 'golf', label: 'Golf', emoji: '⛳', enabled: false, seasonLabel: 'Coming Soon' },
];

interface SportContextType {
  activeSport: Sport;
  sportConfig: SportConfig;
  setSport: (sport: Sport) => void;
  allSports: SportConfig[];
}

const SportContext = createContext<SportContextType | undefined>(undefined);

export const SportProvider = ({ children }: { children: ReactNode }) => {
  const [activeSport, setActiveSport] = useState<Sport>('nfl');

  const setSport = useCallback((sport: Sport) => {
    const config = SPORTS.find(s => s.id === sport);
    if (config?.enabled) {
      setActiveSport(sport);
    }
  }, []);

  const sportConfig = SPORTS.find(s => s.id === activeSport)!;

  const value = useMemo(() => ({
    activeSport,
    sportConfig,
    setSport,
    allSports: SPORTS,
  }), [activeSport, sportConfig, setSport]);

  return (
    <SportContext.Provider value={value}>
      {children}
    </SportContext.Provider>
  );
};

export const useSport = () => {
  const context = useContext(SportContext);
  if (context === undefined) {
    throw new Error('useSport must be used within a SportProvider');
  }
  return context;
};
